import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { CreateBillingProfileModalLoader } from '@css-xp/ppc-page-loaders';
import { CreateInvoiceGroupRequest } from '@atlassian/cssxp-types-billing';
import Modal, { ModalTransition, ModalBody } from '@atlaskit/modal-dialog';
import { createInvoiceGroup } from '@atlassian/cssxp-proxies';
import { createContactRelationship } from '@atlassian/temp-proxies';
import { fetchShipToInPPC } from '@css-xp/ppc-sold-to-address-table';
import { CreateBillingProfilePane, CreateBillingProfilePaneV2 } from '@css-xp/ppc-billing-profile-table';
import { CCP_SEARCH_MAX_LIMIT } from '@css-xp/ppc-constants';
import { FetchShipToInPpcQuery, FetchShipToInPpcQueryVariables } from '@css-xp/graphtypes/src/generated-types-new';
import { SoldToAddressesType } from '@css-xp/ppc-select-customer-address';
import { useContactServicePayload } from '@css-xp/contact-resources';
import { CreateContactRelationshipsRequest } from '@css-xp/contact-resources/src/constant';
import { failureMessage, getBillingProfileUrl, getSupportUrl } from '@css-xp/ppc-common';
import { errorNotificationBuilder, NotificationTypes, successNotificationBuilder, useNotification } from '@css-xp/base';
import { ErrorStage } from './create-customer-and-billing-profile-modal';
import { CartCustomerSearchItem } from '../types';
import { BillingProfileResponse } from '../../../../types';
import './create-billing-profile-modal.scss';

interface Props {
  isOpen: boolean;
  transactionAccountId: string;
  onClose: () => void;
  customer: CartCustomerSearchItem;
  onSuccess: (responsePayload?: { billingProfileInfo: BillingProfileResponse }) => void;
  transactionAccountData?: any;
  transactionAccountLoading?: boolean;
  isDesignIteration1Enabled?: boolean;
  isSoldToAddressArchivalEnabled?: boolean;
}

const StyledBillingProfilePanelV2 = styled.div`
  height: 100%;
  .create-billing-profile-form {
    height: 100%;
  }
`;

export const getErrorMessage = (errorStage: ErrorStage) => {
  switch (errorStage) {
    case ErrorStage.BILLING_PROFILE_STEP:
      return (
        <>
          Something went wrong while creating the billing profile. Please select the customer again and try to create
          the billing profile once more, or contact {getSupportUrl()}.
        </>
      );
    case ErrorStage.CONTACT_RELATIONSHIP_STEP:
      return (
        <>
          Billing profile created successfully, something went wrong while creating contacts. Please visit the billing
          profile details page to add contacts or contact {getSupportUrl()}.
        </>
      );
    default:
      return failureMessage();
  }
};

export const CreateBillingProfileModalForOrderFlow = ({
  isOpen,
  transactionAccountId,
  onClose,
  onSuccess,
  customer,
  transactionAccountData,
  transactionAccountLoading,
  isDesignIteration1Enabled,
  isSoldToAddressArchivalEnabled,
}: Props) => {
  const { getCreateContactRelationshipsPayload } = useContactServicePayload();
  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const errorStage = React.useRef(ErrorStage.BILLING_PROFILE_STEP);

  const { data: soldToParties, loading: soldToPartiesLoading } = useQuery<
    FetchShipToInPpcQuery,
    FetchShipToInPpcQueryVariables
  >(fetchShipToInPPC, {
    variables: {
      searchParams: {
        companyName: customer?.name,
        transactionAccountId: transactionAccountId,
        ...(isSoldToAddressArchivalEnabled && { isActive: true }),
      },
      pageParams: {
        size: CCP_SEARCH_MAX_LIMIT,
      },
    },
    displayName: 'SoldToAddressForCompanyNameQuery',
    // we need to fetch the shipToParties even when sold to address is fetched for a single address id
    skip: !transactionAccountId || !customer?.name,
  });

  const customerAddresses = soldToParties?.shipToParty?.nodes as SoldToAddressesType;

  const onSubmitCreateBillingProfile = async (requestForm: CreateInvoiceGroupRequest, contactServicePayload?: any) => {
    try {
      setIsSubmitLoading(true);
      const billingProfileResponse = await createInvoiceGroup(requestForm);
      // create contact relationship
      if (billingProfileResponse?.id) {
        //on successfull creation of Billing Profile
        !isDesignIteration1Enabled &&
          onSuccess({
            billingProfileInfo: billingProfileResponse,
          });
        errorStage.current = ErrorStage.CONTACT_RELATIONSHIP_STEP;
        const createPayload = getCreateContactRelationshipsPayload({
          invoiceGroupId: billingProfileResponse?.id,
          ...contactServicePayload,
        });
        const contactServiceResponse = await createContactRelationship(
          createPayload as CreateContactRelationshipsRequest
        );
        if (contactServiceResponse) {
          setIsSubmitLoading(false);
        }
        /** for new design changes , calling onSuccess only when all the operations are successful as there is screen switching. */
        isDesignIteration1Enabled && onSuccess({ billingProfileInfo: billingProfileResponse });
        const successMessage = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withId('create-invoice-group-success')
          .withHeader(
            <>
              Created billing profile{' '}
              {getBillingProfileUrl(billingProfileResponse?.name, billingProfileResponse?.id, transactionAccountId)}.
            </>
          )
          .withDismissAfter(5000)
          .build();
        sendSuccessNotification(successMessage);
        setNotificationSent(true);
        onClose();
      }
    } catch (err) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update unsuccessful')
          .withMessage(getErrorMessage(errorStage.current))
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
        onClose();
      }
    }
  };

  const renderCreateBillingProfileModal = () => {
    return (
      <ModalTransition>
        {isOpen && (
          <Modal
            onClose={onClose}
            testId="add-customer-modal"
            // shouldCloseOnOverlayClick={false}
            // shouldScrollInViewport
            height="600px"
          >
            {soldToPartiesLoading || transactionAccountLoading ? (
              <ModalBody>
                <CreateBillingProfileModalLoader />
              </ModalBody>
            ) : (
              <StyledBillingProfilePanelV2>
                <CreateBillingProfilePaneV2
                  transactionAccountId={transactionAccountId}
                  transactionAccountDetails={transactionAccountData}
                  soldToAddresses={customerAddresses}
                  isTermPaymentSelectionDisabled={true}
                  closeModal={onClose}
                  onSubmit={onSubmitCreateBillingProfile}
                  showDefaultProgressIndicator={true}
                  isSubmitLoading={isSubmitLoading}
                  showMonthlyDefaultSelected={true}
                />
              </StyledBillingProfilePanelV2>
            )}
          </Modal>
        )}
      </ModalTransition>
    );
  };

  return isDesignIteration1Enabled ? (
    renderCreateBillingProfileModal()
  ) : (
    <ModalTransition>
      {isOpen && !soldToPartiesLoading && !transactionAccountLoading && (
        <Modal onClose={onClose} testId="add-customer-modal" shouldCloseOnOverlayClick={false} shouldScrollInViewport>
          <div className="create-billing-profile-modal">
            <CreateBillingProfilePane
              transactionAccountId={transactionAccountId}
              transactionAccountDetails={transactionAccountData}
              isProgressIndicatorEnabled={true}
              onSubmit={onSubmitCreateBillingProfile}
              soldToAddresses={customerAddresses}
              isSubmitLoading={isSubmitLoading}
              isTermPaymentSelectionDisabled={true}
            />
          </div>
        </Modal>
      )}
    </ModalTransition>
  );
};
